import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, Shield } from 'lucide-react';
import { privacyApi, type PrivacyNotice } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { SafeGuideMarkdown } from '../guides/safeGuideMarkdown';

export default function PrivacyAcknowledge() {
  const { t, i18n } = useTranslation('common');
  const { profile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [notice, setNotice] = useState<PrivacyNotice | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    privacyApi.me()
      .then((me) => {
        if (cancelled) return;
        const current = me.notices.find((item) => item.slug === 'privacy-notice') || null;
        setNotice(current);
        if (!me.require_ack) {
          navigate(homeForRole(profile?.role), { replace: true });
        }
      })
      .catch(() => {
        if (!cancelled) setError(t('privacy.loadFailed'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [i18n.language, navigate, profile?.role, t]);

  const handleAck = async () => {
    if (!notice) return;
    setBusy(true);
    setError(null);
    try {
      await privacyApi.acknowledge(notice.slug);
      const from = new URLSearchParams(location.search).get('from');
      if (from && from.startsWith('/') && !from.startsWith('/error/')) {
        navigate(from, { replace: true });
        return;
      }
      navigate(homeForRole(profile?.role), { replace: true });
    } catch {
      setError(t('privacy.actionFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-10">
      <div className="table-surface rounded-2xl border border-slate-200 p-6 dark:border-slate-700">
        <div className="mb-4 flex items-center gap-2">
          <Shield className="h-5 w-5 text-primary-600" />
          <h1 className="text-xl font-semibold text-slate-900 dark:text-white">{t('privacy.ackTitle')}</h1>
        </div>
        {loading ? (
          <p className="text-sm text-slate-500">{t('privacy.loading')}</p>
        ) : notice ? (
          <div className="max-h-[60vh] overflow-y-auto">
            <SafeGuideMarkdown text={notice.body} />
          </div>
        ) : (
          <p className="text-sm text-slate-500">{t('privacy.noNotice')}</p>
        )}
        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
        <button
          type="button"
          onClick={() => void handleAck()}
          disabled={busy || !notice}
          className="mt-6 inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {t('privacy.acknowledge')}
        </button>
      </div>
    </div>
  );
}

function homeForRole(role?: string) {
  if (role === 'candidate') return '/candidate';
  if (role === 'invigilator') return '/invigilator';
  return '/admin';
}
